'use client'

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import type { OrgContextData } from '@/hooks/use-org-context'

interface OrgProviderValue {
  org: OrgContextData['organization'] | null
  user: OrgContextData['user'] | null
  organizations: OrgContextData['organizations']
  loading: boolean
  error: string | null
}

const OrgContext = createContext<OrgProviderValue>({
  org: null,
  user: null,
  organizations: [],
  loading: true,
  error: null,
})

export function OrgProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<OrgProviderValue>({
    org: null,
    user: null,
    organizations: [],
    loading: true,
    error: null,
  })

  const load = useCallback(async () => {
    setState((current) => ({ ...current, loading: true }))

    try {
      const res = await fetch('/api/me')
      if (!res.ok) {
        setState((current) => ({ ...current, loading: false, error: 'Not authenticated' }))
        return
      }
      const ct = res.headers.get('content-type') ?? ''
      if (!ct.includes('application/json')) {
        setState((current) => ({ ...current, loading: false, error: 'Not authenticated' }))
        return
      }
      const data: OrgContextData = await res.json()
      setState({
        org: data.organization,
        user: data.user,
        organizations: data.organizations ?? [],
        loading: false,
        error: null,
      })
    } catch (err) {
      console.error('[OrgProvider]', err)
      setState((current) => ({
        ...current,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load org context',
      }))
    }
  }, [])

  useEffect(() => {
    void load()

    const handleWorkspaceSwitched = () => {
      void load()
    }

    document.addEventListener('mh:workspace-switched', handleWorkspaceSwitched)
    return () => {
      document.removeEventListener('mh:workspace-switched', handleWorkspaceSwitched)
    }
  }, [load])

  return (
    <OrgContext.Provider value={state}>
      {children}
    </OrgContext.Provider>
  )
}

export const useOrg = () => useContext(OrgContext)
